import { useQuery } from '@apollo/client';
import { Alert, Box, Text, Stack, TextLink } from 'braid-design-system';
import { useState } from 'react';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import {
  type SavedListing,
  GetSavedListingsDocument,
} from 'src/generated/graphql';

import NoResultsPanel from './components/NoResultsPanel';
import SavedBusiness from './components/SavedItem';
import SortDropdown from './components/SortDropdown';

type Sort = 'Date saved' | 'Date posted';

const SavedBusinesses = () => {
  const { data, loading, error } = useQuery(GetSavedListingsDocument);
  const [sortBy, setSortMethod] = useState<Sort>('Date saved');

  if (loading) {
    return <CustomLoader />;
  }

  if (error || !data) {
    return (
      <Alert tone="critical">
        <Text>
          There was an error loading your saved businesses. If this issue
          persists please{' '}
          <TextLink
            href="https://www.seekbusiness.com.au/contact-us"
            hitArea="large"
          >
            contact us
          </TextLink>
        </Text>
      </Alert>
    );
  }

  if (data.savedListings.length === 0) {
    return <NoResultsPanel />;
  }

  const sortedListings = sortListings(data.savedListings, sortBy);

  return (
    <Stack space="medium">
      {sortedListings.length > 1 && (
        <Box paddingRight="medium">
          <SortDropdown value={sortBy} onChange={setSortMethod} />
        </Box>
      )}

      {sortedListings.map((listing) => (
        <SavedBusiness key={listing.listingId} listing={listing} />
      ))}
    </Stack>
  );
};
export default SavedBusinesses;

// ============================================================================
// Helpers
// ============================================================================
const sortListings = (listings: SavedListing[], sortBy: string) => {
  if (sortBy === 'Date posted') {
    return [...listings].sort(function (x: SavedListing, y: SavedListing) {
      const xDate = new Date(x.createdDate ?? new Date());
      const yDate = new Date(y.createdDate ?? new Date());
      return xDate.getTime() > yDate.getTime() ? -1 : 1;
    });
  }

  return [...listings].sort(function (x: SavedListing, y: SavedListing) {
    const xDate = new Date(x.savedDate ?? new Date());
    const yDate = new Date(y.savedDate ?? new Date());
    return xDate.getTime() > yDate.getTime() ? -1 : 1;
  });
};
